import React, { useState, useMemo } from 'react';
import { Card, Table, Badge, Form, Row, Col } from 'react-bootstrap';

function PersonalRecordsTable({ personalRecords, onRefresh }) {
  const [sortConfig, setSortConfig] = useState({ key: 'achieved_date', direction: 'desc' });
  const [filterType, setFilterType] = useState('all'); // 'all', 'weight_reps', 'cardio_basic', etc.

  // Filter records by exercise type
  const filteredRecords = useMemo(() => {
    if (filterType === 'all') return personalRecords;
    return personalRecords.filter(pr => pr.exercise_type === filterType);
  }, [personalRecords, filterType]);

  // Sort records
  const sortedRecords = useMemo(() => {
    const sorted = [...filteredRecords];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'achieved_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredRecords, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatRecordValue = (pr) => {
    const value = parseFloat(pr.value);
    const unit = pr.unit || '';

    switch (pr.record_type) {
      case 'max_weight':
        return `${value} ${unit}`;
      case 'max_reps':
        return `${value} reps`;
      case 'max_distance':
        return `${value} ${unit}`;
      case 'best_pace':
        // Value can be stored in two formats:
        // 1. As distance/second (e.g., 0.00138 miles/sec) - need to convert to min/mile
        // 2. As min/mile or min/km directly (e.g., 9.87 min/miles) - just format

        if (value > 0 && unit.includes('/sec')) {
          // Format 1: Convert from distance/sec to min/unit
          const secondsPerUnit = 1 / value; // Convert from units/sec to sec/unit
          const minutesPerUnit = secondsPerUnit / 60; // Convert to minutes per unit
          const distanceUnit = unit.replace('/sec', ''); // Extract "miles" or "km" from "miles/sec"

          const mins = Math.floor(minutesPerUnit);
          const secs = Math.round((minutesPerUnit - mins) * 60);
          return `${mins}:${secs.toString().padStart(2, '0')} min/${distanceUnit}`;
        } else if (unit.includes('min/')) {
          // Format 2: Already in min/unit format, just convert decimal to MM:SS
          const mins = Math.floor(value);
          const secs = Math.round((value - mins) * 60);
          return `${mins}:${secs.toString().padStart(2, '0')} ${unit}`;
        }
        return `${value.toFixed(2)} ${unit}`;
      case 'longest_duration':
        // Value is stored in seconds, not minutes
        const hours = Math.floor(value / 3600);
        const minutes = Math.floor((value % 3600) / 60);
        const seconds = Math.round(value % 60);
        if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`;
        } else {
          return `${seconds}s`;
        }
      default:
        return `${value} ${unit}`;
    }
  };

  const getRecordTypeBadge = (recordType) => {
    const badges = {
      max_weight: { variant: 'primary', icon: 'fa-weight-hanging', label: 'Max Weight' },
      max_reps: { variant: 'success', icon: 'fa-redo', label: 'Max Reps' },
      max_distance: { variant: 'info', icon: 'fa-road', label: 'Max Distance' },
      best_pace: { variant: 'warning', icon: 'fa-tachometer-alt', label: 'Best Pace' },
      longest_duration: { variant: 'secondary', icon: 'fa-clock', label: 'Longest Duration' }
    };

    const badge = badges[recordType] || { variant: 'dark', icon: 'fa-trophy', label: recordType };

    return (
      <Badge bg={badge.variant} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
        <i className={`fas ${badge.icon}`}></i>
        <span>{badge.label}</span>
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              <i className="fas fa-trophy me-2"></i>
              Personal Records ({sortedRecords.length})
            </h5>
          </Col>
          <Col md="auto">
            <Form.Select
              size="sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="all">All Types</option>
              <option value="weight_reps">Strength (Weight/Reps)</option>
              <option value="weight_time">Strength (Weight/Time)</option>
              <option value="cardio_basic">Cardio</option>
              <option value="cardio_segments">Cardio (Intervals)</option>
            </Form.Select>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-0">
        {sortedRecords.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-trophy fa-3x text-muted mb-3"></i>
            <p className="text-muted">No personal records yet. Keep training!</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th onClick={() => handleSort('exercise_name')} style={{ cursor: 'pointer' }}>
                    Exercise {sortConfig.key === 'exercise_name' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('record_type')} style={{ cursor: 'pointer' }}>
                    Record Type {sortConfig.key === 'record_type' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('value')} style={{ cursor: 'pointer' }}>
                    Value {sortConfig.key === 'value' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('achieved_date')} style={{ cursor: 'pointer' }}>
                    Achieved {sortConfig.key === 'achieved_date' && (
                      <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.map((pr) => (
                  <tr key={pr.id}>
                    <td>
                      <strong>{pr.exercise_name}</strong>
                      <br />
                      <small className="text-muted">{pr.exercise_type.replace('_', ' ')}</small>
                    </td>
                    <td>{getRecordTypeBadge(pr.record_type)}</td>
                    <td><strong className="text-primary">{formatRecordValue(pr)}</strong></td>
                    <td>{formatDate(pr.achieved_date)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default PersonalRecordsTable;
